import type { QueryClient } from "@tanstack/react-query";
import { getAvailabilityPrivateLessonsRange } from "@/lib/services/teacherAvailabilityLessonRange";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import { TeachersAvailabilityApi } from "@/openapi/generated";
import { prefetchAppointmentHours } from "./prefetchAppointmentHours";
import { prefetchPrivateLessonsCalendar } from "./prefetchPrivateLessonsCalendar";

const SPECIAL_HOURS_PAGE_SIZE = 500;

function getAvailabilityLessonFilters(teacherId: number) {
  return {
    ...getAvailabilityPrivateLessonsRange(),
    teacherId,
  };
}

/** Server prefetch for teacher availability `/admin/dashboard/availability`. */
export async function prefetchTeacherAvailability(
  queryClient: QueryClient,
  teacherId: number,
  accessToken: string,
  timeZone?: string | null,
) {
  const service = createAuthenticatedApi(TeachersAvailabilityApi, accessToken);
  const lessonFilters = getAvailabilityLessonFilters(teacherId);

  await Promise.allSettled([
    queryClient.prefetchQuery({
      queryKey: [
        QueryKeys.SpecialHours,
        teacherId,
        1,
        SPECIAL_HOURS_PAGE_SIZE,
        undefined,
        undefined,
      ],
      queryFn: () =>
        service.apiAvailabilityTeacherIdSpecialAppointmentHoursGet({
          teacherId,
          page: 1,
          pageSize: SPECIAL_HOURS_PAGE_SIZE,
        }),
      staleTime: 5 * 60 * 1000,
    }),
    prefetchAppointmentHours(teacherId, queryClient, accessToken),
    prefetchPrivateLessonsCalendar(
      queryClient,
      accessToken,
      lessonFilters,
      timeZone,
    ),
  ]);

  return queryClient;
}
